export interface ProjectMemberResponse {
    _id: string;
    project_id: string;
    invited_user: string;
    invited_user_name?: string;
    role: string;
    invite_status: string;
    invited_by: string;
    payment_type?: string;
    invited_at?: number;
    accepted_at?: number;
    rejected_at?: number;
    revoked_at?: number;
    left_at?: number;
    is_deleted?: number;
}

export interface ProjectMemberWithOwnerResponse extends ProjectMemberResponse {
    project_owner_name?: string;
}

export interface ProjectMemberListResponse {
    members: ProjectMemberResponse[];
    total_count: number;
}

export class ProjectMembersApiService {
    private static readonly BASE_URL = '/api/projects';

    private static async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
        const token = localStorage.getItem('token');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options.headers,
        };

        const response = await fetch(url, { ...options, headers });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: await response.text() };
            }
            // Throw object compatible with UI expectations (error.message.detail)
            throw { message: errorData, status: response.status };
        }
        return response;
    }

    static async getProjectMembers(
        projectId: string,
        filters?: {
            invite_status?: string;
            is_deleted?: boolean;
        }
    ): Promise<ProjectMemberListResponse> {
        const queryParams = new URLSearchParams();
        if (filters?.invite_status) queryParams.append('invite_status', filters.invite_status);
        if (filters?.is_deleted !== undefined) queryParams.append('is_deleted', String(filters.is_deleted));

        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        const url = `${this.BASE_URL}/${projectId}/members${queryString}`;

        const response = await this.fetchWithAuth(url);
        return response.json();
    }
    static async updateMemberRole(
        projectId: string,
        memberId: string,
        role: string
    ): Promise<ProjectMemberResponse> {
        const url = `${this.BASE_URL}/${projectId}/members/${memberId}/role`;
        const response = await this.fetchWithAuth(url, {
            method: 'PATCH',
            body: JSON.stringify({ role }),
        });
        return response.json();
    }

    static async removeMember(
        projectId: string,
        memberId: string
    ): Promise<void> {
        // Map "remove" action to revoking the invitation
        const url = `${this.BASE_URL}/${projectId}/members/${memberId}/invite`;
        await this.fetchWithAuth(url, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'revoked' }),
        });
    }

    static async inviteMember(
        projectId: string,
        emails: string[],
        role: string,
        paymentType: string
    ): Promise<any> {
        const url = `${this.BASE_URL}/${projectId}/members/invite`;
        const response = await this.fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({
                users: emails,
                role: role,
                payment_type: paymentType,
            }),
        });
        return response.json();
    }

    static async updateShareableLinkSetting(
        projectId: string,
        enabled: boolean
    ): Promise<void> {
        const url = `${this.BASE_URL}/${projectId}`;
        await this.fetchWithAuth(url, {
            method: 'PUT',
            body: JSON.stringify({
                readonly_sharable_link_enabled: enabled
            }),
        });
    }
}
